import React, { useEffect, useState } from "react";
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { Input } from '@mui/material';
import PortfolioCard from './PortfolioCard';
import TabNavigation from './TabNavigation';
import MarketDataDialog from '../orders/MarketDataDialog';
import Footer from '../Footer';
import PortfolioList from "./PortfolioList";
import { closeAllOrders, getMarketData } from '../../services/allApiService';
import { normalizeExchange } from "../orders/orderUtils.js";
// Socket bridge: lets us pause REST polling and live-merge ticks into local state.
import { isMarketSocketConnected, onTickBatch } from "../../services/socket/marketSocket";
import CloseAllConfirmationModal from "./CloseAllConfirmationModal.js";
import CloseAllResultsModal from "./CloseAllResultsModal.js"; // Import the new modal
import { useDispatch, useSelector } from 'react-redux';
import {
    setActiveTab,
    fetchCurrentPositions,
    fetchSquaredOffPositions,
    fetchMarketData,
    closeAllOrdersAction,
    clientPositionsGet,
} from '../../actions/portfolioActions';

function Portfolio() {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [activeTabList, setActiveTabList] = useState("ALL");
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [marketData, setMarketData] = useState(null);
    const [showCloseAllModal, setShowCloseAllModal] = useState(false);
    const [isClosingOrders, setIsClosingOrders] = useState(false);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    // New state for results modal
    const [showResultsModal, setShowResultsModal] = useState(false);
    const [closeAllResults, setCloseAllResults] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");

    // Get data from Redux store
    const {
        orders,
        fetchData,
        error,
        exchangeTokens,
        activeTab,
    } = useSelector((state) => state.portfolio);


    const initialized = React.useRef(false);

    useEffect(() => {
        if (initialized.current) return;
        initialized.current = true;

        dispatch({ type: 'INITIALIZE_PORTFOLIO', payload: { activeTabList } });
    }, [dispatch, activeTabList]);

    useEffect(() => {

        const interval = setInterval(() => {
            dispatch(clientPositionsGet());
        }, 3000);

        return () => clearInterval(interval);

    }, []);

    useEffect(() => {
        if (Object.keys(exchangeTokens).length === 0 || activeTab === 'square_off') return;

        const interval = setInterval(() => {
            // Skip REST poll while the socket is delivering ticks
            // (marketSocketSaga keeps state.portfolio.fetchData live).
            if (isMarketSocketConnected()) return;
            dispatch(fetchMarketData(exchangeTokens));
        }, 1000);

        return () => clearInterval(interval);
    }, [exchangeTokens, dispatch, activeTab]);

    // Refresh data when refreshTrigger changes (after closing orders)
    useEffect(() => {
        if (refreshTrigger > 0) {
            dispatch(fetchCurrentPositions(activeTabList));
            dispatch(fetchSquaredOffPositions(activeTabList));
        }
    }, [refreshTrigger, dispatch, activeTabList]);

    const handleTabChange = (tab) => {
        if (activeTab === tab) return;

        const tabMapping = {
            "Active": "current",
            "square_off": "square_off"
        };

        const mappedTab = tabMapping[tab] || "current";
        dispatch(setActiveTab(mappedTab));
    };

    const handleTabListChange = (list) => {
        if (activeTabList === list) return;
        setActiveTabList(list);
        setRefreshTrigger(prev => prev + 1);
    };

    const handleCardClick = (item, marketData) => {
        if (activeTab === "square_off") return;

        const orderPrice = item.type === 'buy' ? marketData[0]?.depth?.buy[0]?.price : marketData[0]?.depth?.sell[0]?.price;

        navigate('/add-order', {
            state: {
                o_buttonType: item.type === 'buy' ? 'sell' : 'buy',
                o_scriptName: item?.script_name,
                o_exchange: item?.exchange,
                o_price: orderPrice,
                o_lotssize: item?.lots,
                o_netChange: marketData[0]?.netChange,
                o_percentChange: marketData[0]?.percentChange,
                o_script_id: item?.script_id,
                o_exchange_id: item?.exchange_id,
                o_qty: item?.qty,
                o_expiry: item?.expiry,
                o_token: item?.token,
                o_activeTabValue: activeTab,
                o_lowerCircuit: marketData[0]?.lowerCircuit,
                o_upperCircuit: marketData[0]?.upperCircuit,
                o_high: marketData[0]?.high,
                o_low: marketData[0]?.low,
                o_order_id: item?.order_id,
                o_from_portfolio: 'portfolio',
                o_script_name: item?.script_name
            },
        });
        setSelectedOrder(item);
    };

    useEffect(() => {
        let intervalId;
        let offTick = null;
        if (!selectedOrder) return;

        const targetToken = String(selectedOrder.token || selectedOrder.symbolToken);

        const fetchData = async () => {
            const normalizedExchange = normalizeExchange(selectedOrder.exchange);
            const mode = "FULL";
            const exchangeTokens = { [normalizedExchange]: [selectedOrder.token || selectedOrder.symbolToken] };

            try {
                const response = await getMarketData(mode, exchangeTokens);
                const fetchedMarketData = response.data?.fetched || [];
                if (fetchedMarketData.length > 0) {
                    setMarketData(fetchedMarketData);
                }
            } catch (error) {
                console.error("Failed to fetch market data:", error);
                setMarketData(null);
            }
        };

        // 1. One-time snapshot so the dialog shows the LAST price immediately
        //    (works even when the market is closed). The socket then updates it
        //    live; the REST poll only runs while the socket is down.
        fetchData();

        // 2. Ongoing 1s poll, but only when the socket is NOT delivering ticks.
        intervalId = setInterval(() => {
            if (isMarketSocketConnected()) return;
            fetchData();
        }, 1000);

        // 3. While the socket IS delivering ticks, merge them into the
        //    dialog's local marketData state so the dialog stays live too.
        offTick = onTickBatch((batch) => {
            const tick = batch.get(targetToken);
            if (!tick) return;
            setMarketData((prev) => {
                if (!Array.isArray(prev) || prev.length === 0) return prev;
                return prev.map((row) => {
                    if (String(row.symbolToken) !== targetToken) return row;
                    return {
                        ...row,
                        ltp: tick.ltp ?? row.ltp,
                        high: tick.high ?? row.high,
                        low: tick.low ?? row.low,
                        upperCircuit: tick.upperCircuit ?? row.upperCircuit,
                        lowerCircuit: tick.lowerCircuit ?? row.lowerCircuit,
                        depth: tick.depth ?? row.depth,
                    };
                });
            });
        });

        return () => {
            if (intervalId) clearInterval(intervalId);
            if (offTick) offTick();
        };
    }, [selectedOrder]);

    const handleCloseAllClick = () => {
        setShowCloseAllModal(true);
    };

    const handleCloseAllConfirm = async () => {
        setIsClosingOrders(true);
        try {
            const response = await closeAllOrders();

            // Wait 2 seconds before refreshing
            // await new Promise(resolve => setTimeout(resolve, 2000));
            // console.log('response', response);
            // debugger
            if (response?.success === true) {
                setCloseAllResults(response);
                // Refresh data
                // setRefreshTrigger(prev => prev + 1);
            }
            // Store the results

            // Close confirmation modal
            setShowCloseAllModal(false);

            // Show results modal
            setShowResultsModal(true);

        } catch (error) {
            console.error('Failed to close all orders:', error);
            // You can show an error message here if needed
        } finally {
            setIsClosingOrders(false);
        }
    };

    const handleCloseAllCancel = () => {
        setShowCloseAllModal(false);
    };

    const handleResultsModalClose = () => {
        setShowResultsModal(false);
        setCloseAllResults(null);
        setRefreshTrigger(prev => prev + 1);
    };

    const filteredOrders = orders?.filter((order) => {
        if (!searchQuery) return true;

        const query = searchQuery.toLowerCase();

        return (
            order.symbol?.toLowerCase().includes(query) ||
            order.script_name?.toLowerCase().includes(query) ||
            String(order.lots).includes(query) ||
            String(order.qty).includes(query)
        );
    });

    const displayActiveTab = activeTab === 'current' ? 'Active' : activeTab;

    return (
        <div className="flex items-center justify-center flex-col bg-black min-h-screen p-0">
            <div className="flex flex-col h-[100vh] max-w-sm min-w-[370px] w-full bg-gray-100 p-4">

                <div className="flex items-center gap-3 w-full">
                    <div className="">
                        <h3 className="text-[1rem] font-lato  font-semibold">Portfolio</h3>
                    </div>
                    <div className="flex items-center w-full">
                        <div className="flex items-center w-full" >
                            <Input
                                endAdornment={<Search sx={{ bottom: '13px', right: '13px', fontSize: '12px' }} />}
                                disableUnderline
                                placeholder="Search by symbol, lots or qty"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}

                                sx={{
                                    width: '100%',
                                    padding: '10px 13px 10px 30px',
                                    backgroundColor: 'white',
                                    borderRadius: '50px',
                                    lineHeight: '18px',
                                    height: '25%',
                                    fontSize: '12px',
                                }}
                            />
                        </div>
                    </div>
                </div>

                <PortfolioCard />

                <TabNavigation
                    activeTab={displayActiveTab}
                    activeTabList={activeTabList}
                    onTabChange={handleTabChange}
                    onTabListChange={handleTabListChange}
                    handleCloseAllClick={handleCloseAllClick}
                />
                <div
                    className="flex-1 overflow-y-auto 
                [&::-webkit-scrollbar]:w-1 
                [&::-webkit-scrollbar-track]:bg-transparent 
                [&::-webkit-scrollbar-thumb]:bg-transparent 
                scrollbar-thin scrollbar-track-transparent scrollbar-thumb-transparent"
                >
                    <PortfolioList
                        activeTab={displayActiveTab}
                        orders={filteredOrders}
                        fetchData={fetchData}
                        onCardClick={handleCardClick}
                        error={error}
                    />
                </div>
            </div>

            <Footer />

            {/* Close All Confirmation Modal */}
            <CloseAllConfirmationModal
                isOpen={showCloseAllModal}
                onClose={handleCloseAllCancel}
                onConfirm={handleCloseAllConfirm}
                isLoading={isClosingOrders}
            />

            {/* Close All Results Modal */}
            <CloseAllResultsModal
                isOpen={showResultsModal}
                onClose={handleResultsModalClose}
                results={closeAllResults}
            />
        </div>
    );
}

export default Portfolio;